import React from "react"
import { useState, useEffect } from "react"
import { Card, Container, Row, Col, Button, Badge, Spinner } from "react-bootstrap"
import { Link } from "react-router-dom"
import {
  FaCalendarAlt,
  FaFileAlt,
  FaHeart,
  FaBell,
  FaUser,
  FaPhone,
  FaClock,
  FaMapMarkerAlt,
  FaStethoscope,
  FaChartLine,
  FaPills,
  FaArrowRight,
} from "react-icons/fa"
import { useAuth } from "../../context/AuthContext"
import apiService from "../../services/api"

export default function PatientDashboard() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [notifications, setNotifications] = useState([])
  const [upcomingAppointments, setUpcomingAppointments] = useState([])
  const [healthMetrics, setHealthMetrics] = useState([])
  const [appointmentStats, setAppointmentStats] = useState({})

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        
        // Fetch notifications
        const notificationsResponse = await apiService.getNotifications(1, 5)
        if (notificationsResponse.success) {
          setNotifications(notificationsResponse.data)
        }

        // Fetch appointments
        const appointmentsResponse = await apiService.getAppointments(1, 5, 'scheduled')
        if (appointmentsResponse.success) {
          setUpcomingAppointments(appointmentsResponse.data)
        }

        // Fetch health metrics
        if (user) {
          const metricsResponse = await apiService.getPatientHealthMetrics(user.id)
          if (metricsResponse.success) {
            setHealthMetrics(metricsResponse.data.slice(0, 3))
          }
        }

        // Fetch appointment stats
        const statsResponse = await apiService.getAppointmentStats()
        if (statsResponse.success) {
          setAppointmentStats(statsResponse.data)
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const getNotificationIcon = (type) => {
    switch (type) {
      case "appointment":
        return <FaCalendarAlt />
      case "medication":
        return <FaPills />
      case "report":
        return <FaFileAlt />
      default:
        return <FaBell />
    }
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#f8fffe" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Welcome Header */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
            }}
          >
            {/* Background Pattern */}
            <div className="position-absolute w-100 h-100 top-0 start-0">
              <div
                className="position-absolute rounded-circle opacity-10"
                style={{
                  width: "200px",
                  height: "200px",
                  background: "white",
                  top: "-50px",
                  right: "-50px",
                }}
              ></div>
              <div
                className="position-absolute rounded-circle opacity-10"
                style={{
                  width: "150px",
                  height: "150px",
                  background: "white",
                  bottom: "-30px",
                  left: "-30px",
                }}
              ></div>
            </div>

            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaUser className="me-2" />
                    Patient Portal
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">Welcome back, {user?.name || 'Patient'}!</h1>
                <p className="fs-5 opacity-90 mb-4">
                  Here's an overview of your health journey and upcoming appointments.
                </p>
                <div className="d-flex gap-3">
                  <Button
                    variant="light"
                    className="px-4 py-2 rounded-pill fw-medium"
                    style={{
                      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <FaCalendarAlt className="me-2" style={{ color: "#0891b2" }} />
                    <span style={{ color: "#0891b2" }}>Book Appointment</span>
                  </Button>
                  <Button
                    variant="outline-light"
                    className="px-4 py-2 rounded-pill fw-medium"
                    style={{
                      borderWidth: "2px",
                      backdropFilter: "blur(10px)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <FaPhone className="me-2" />
                    Emergency Call
                  </Button>
                  <Link to="/patient/profile">
                  <Button
                //   variant="outline-light"
                    className="px-4 py-2 rounded-pill fw-medium border-none"
                      style={{
                        borderWidth: "2px",
                        backdropFilter: "blur(10px)",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      }}
                  >
                    Edit Profile
                  </Button>
                  </Link>
                </div>
              </Col>
              <Col md={4} className="text-center">
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style={{
                    width: "120px",
                    height: "120px",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FaUser size={48} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <Row className="g-4">
          {/* Quick Stats */}
          <Col lg={8}>
            <Row className="g-4 mb-4">
              <Col md={4}>
                <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
                  <Card.Body className="p-4 text-center">
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      }}
                    >
                      <FaCalendarAlt className="text-white" size={24} />
                    </div>
                    <h4 className="fw-bold mb-1">{appointmentStats.totalAppointments || 0}</h4>
                    <p className="text-muted mb-0">Total Appointments</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
                  <Card.Body className="p-4 text-center">
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                      }}
                    >
                      <FaClock className="text-white" size={24} />
                    </div>
                    <h4 className="fw-bold mb-1">{appointmentStats.pendingAppointments || 0}</h4>
                    <p className="text-muted mb-0">Pending Appointments</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
                  <Card.Body className="p-4 text-center">
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      }}
                    >
                      <FaBell className="text-white" size={24} />
                    </div>
                    <h4 className="fw-bold mb-1">{notifications.filter(n => !n.is_read).length}</h4>
                    <p className="text-muted mb-0">Unread Notifications</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Upcoming Appointments */}
            <Card className="border-0 rounded-4 mb-4" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">Upcoming Appointments</h5>
                  <Link to="/patient/appointments">
                    <Button variant="link" className="text-decoration-none p-0">
                      View All <FaArrowRight className="ms-1" />
                    </Button>
                  </Link>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {upcomingAppointments.length > 0 ? (
                  upcomingAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="d-flex align-items-center p-3 rounded-3 mb-3"
                      style={{ backgroundColor: "white" }}
                    >
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                        }}
                      >
                        <FaCalendarAlt className="text-white" size={20} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{appointment.doctor_name}</h6>
                        <p className="text-muted mb-1">
                          {new Date(appointment.appointment_date).toLocaleDateString()} at {appointment.appointment_time}
                        </p>
                        <Badge bg="primary" className="rounded-pill">
                          {appointment.type}
                        </Badge>
                      </div>
                      <div className="text-end">
                        <p className="text-muted mb-1">{appointment.doctor_specialty}</p>
                        <Badge bg="success" className="rounded-pill">
                          {appointment.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <FaCalendarAlt size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No upcoming appointments</h6>
                    <p className="text-muted">Book your next appointment to get started</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Health Metrics */}
            <Card className="border-0 rounded-4" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-0">Health Metrics</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  {healthMetrics.length > 0 ? (
                    healthMetrics.map((metric, index) => (
                      <Col md={4} key={index} className="mb-3">
                        <div
                          className="p-3 rounded-3 text-center"
                          style={{ backgroundColor: "white" }}
                        >
                          <div
                            className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              background: metric.color,
                            }}
                          >
                            <FaHeart className="text-white" size={20} />
                          </div>
                          <h6 className="fw-bold mb-1">{metric.title}</h6>
                          <h4 className="fw-bold mb-1">
                            {metric.value} <small className="text-muted">{metric.unit}</small>
                          </h4>
                          <Badge bg={metric.status === 'normal' ? 'success' : 'warning'} className="rounded-pill">
                            {metric.status}
                          </Badge>
                        </div>
                      </Col>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <FaChartLine size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No health metrics available</h6>
                      <p className="text-muted">Your health metrics will appear here</p>
                    </div>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Notifications Sidebar */}
          <Col lg={4}>
            <Card className="border-0 rounded-4" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">Notifications</h5>
                  <Badge bg="danger" className="rounded-pill">
                    {notifications.filter(n => n.urgent).length}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`d-flex align-items-start p-3 rounded-3 mb-3 ${
                        notification.urgent ? "border border-danger" : ""
                      }`}
                      style={{ backgroundColor: "white" }}
                    >
                      <div
                        className={`rounded-circle me-3 d-flex align-items-center justify-content-center ${
                          notification.urgent ? "bg-danger" : "bg-primary"
                        }`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{notification.title}</h6>
                        <p className="text-muted mb-1">{notification.message}</p>
                        <small className="text-muted">
                          {new Date(notification.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <FaBell size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No notifications</h6>
                    <p className="text-muted">You're all caught up!</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
